// @ts-check
/* global $ module require */
'use strict';

declare var $;
declare var grecaptcha: any;

/**
 * Class to manage ui for individual credit application section.
 */
class CreditAppView {
    /**
     * @var {number} index number for the view within the credit application.
     * @var {boolean} visible flag for whether the section is visible.
     * @var {string} name of the section view.
     * @var {HTMLDivElement} container that holds the section div element.
     * @var {Array<HTMLFormElement} forms array that holds all of the sections forms.
     * @var {HTMLElement} side_nav that holds corresponding side nav element.
     */
    index: number;
    visible: boolean;
    name: string;
    container: HTMLDivElement;
    forms: Array<HTMLFormElement>;
    side_nav: HTMLElement;

    /**
     * Instantiate the class and binds DOM elements.
     *
     * @return {CreditAppView} instantiated and bound.
     */
    constructor(view_name: string, index: number) {
        this.index = index;
        this.visible = false;
        this.name = view_name;
        this.container = null;
        this.forms = new Array<HTMLFormElement>();
        this.side_nav = null;

        this.bind();
        this.bind_forms();
    }

    /**
     * Find all of the forms for this section name and add them to the forms array.
     *
     * @return {void}
     */
    bind_forms(): void {
        const forms = document.getElementsByClassName('js_' + this.name.concat('_form'));
        for (let form of forms) {
            const section_form = <HTMLFormElement> form;
            this.forms.push(section_form);
        }
    }

    /**
     * Binds the ui elements and adds click events for the different form buttons.
     *
     * @return {void}
     */
    bind(): void {
        this.container = <HTMLDivElement> document.getElementById('js_' + this.name.concat('_container'));
        this.side_nav = document.getElementById('js_' + this.name.concat('_nav'));

        // Check if there are any notes radio buttons and bind them to the notes toggle event.
        const radio_buttons = this.container.getElementsByClassName('radio');
        if (radio_buttons.length > 0) {
            for (const radio_button of radio_buttons) {
                radio_button.addEventListener('click', this.toggle_notes.bind(this));
            }
        }

        // Check if there are any add buttons and bind the add and remove events.
        const add_button = this.container.querySelector('#js_' + this.name + '_add_form_section');
        if (add_button) {
            add_button.addEventListener('click', this.add_form_to_section.bind(this));

            const remove_button = this.container.querySelector('#js_' + this.name + '_remove_form_section');
            remove_button.addEventListener('click', this.remove_form_from_section.bind(this));
        }

        // Check if there are any phone inputs and bind input mask event.
        const phone_inputs = this.container.getElementsByClassName('js_phone');
        if (phone_inputs.length > 0) {
            for (const input of phone_inputs) {
                input.addEventListener('input', this.mask_phone_number.bind(this));
            }
        }

        // Check if there are any fein inputs and bind input mask event.
        const fein_inputs = this.container.getElementsByClassName('js_fein');
        if (fein_inputs.length > 0) {
            for (const input of fein_inputs) {
                input.addEventListener('input', this.mask_fein_number.bind(this));
            }
        }

        const google_captcha = document.querySelector('.js_hide_on_print .js-recaptcha');
        google_captcha.classList.add('tv-display__none');
    }

    /**
     * Makes input mask for phone numbers to format to (555) 555-5555.
     *
     * @param {Event} event input event from phone input.
     * @return {void}
     */
    mask_phone_number(event: Event): void {
        const input = <HTMLInputElement> event.target;
        const x = input.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        input.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
    }

    /**
     * Makes input mask for fein numbers to format to 12-3456789.
     *
     * @param {Event} event input event from fein input.
     * @return {void}
     */
    mask_fein_number(event: Event): void {
        const input = <HTMLInputElement> event.target;
        const x = input.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,7})/);
        input.value = !x[2] ? x[1] : x[1] + '-' + x[2];
    }

    /**
     * Updates visibility of the section based of the visible flag passed in.
     *
     * @param {boolean} visible flag for whether section should be visible or not.
     * @return {void}
     */
    toggle_visibility(visible: boolean): void {
        this.visible = visible;

        if (this.visible) {
            this.container.classList.remove('hidden');
            this.side_nav.classList.add('tv-current-credit-application-step');
            return;
        }

        this.container.classList.add('hidden');
        this.side_nav.classList.remove('tv-current-credit-application-step');
    }

    /**
     * Shows or hides the notes fields and removes/adds the required field to the
     * notes field.
     *
     * @param {Event} event click event from notes radio buttons.
     * @return {void}
     */
    toggle_notes(event: Event): void {
        const element = <HTMLInputElement> event.target;
        const notes_container = document.getElementById('js_' + element.dataset.name + '_notes_container');
        const notes_text = document.getElementById('js_' + element.dataset.name + '_notes');

        if (element.value === 'yes') {
            notes_container.classList.remove('hidden');
            notes_text.setAttribute('required', '');
            return;
        }

        notes_container.classList.add('hidden');
        notes_text.removeAttribute('required');
        notes_text.innerHTML = '';
    }

    /**
     * Goes through all of the forms in the section and pulls all of the input values
     * to an object for each form.
     *
     * @return {Array<Object>} array of data from all of the sections form inputs.
     */
    serialize_form_data(): Array<Object> {
        const form_data = [];
        // Iterate through each form in the section view.
        for (let form of this.forms) {
            const data = {};
            // Iterate through each form input element.
            for (let element of form.elements) {
                const form_element = <HTMLInputElement> element;
                if (form_element.name !== 'security_hash') {
                    if (form_element.type === 'checkbox') {
                        data[form_element.name] = form_element.checked ? 'Yes' : 'No';
                        continue;
                    } else if (form_element.type === 'radio' && !form_element.checked) {
                        continue;
                    }
                    data[form_element.name] = form_element.value;
                }
            }
            form_data.push(data);
        }

        return form_data;
    }

    /**
     * Clones the first form element and adds it to the end of the form section list.
     *
     * @return {void}
     */
    add_form_to_section(): void {
        // Make the remove button visible if it isn't already visible.
        const remove_button = this.container.querySelector('#js_' + this.name + '_remove_form_section');
        if (remove_button.classList.contains('hidden')) {
            remove_button.classList.remove('hidden');
        }

        // Clone first form element and clear all of the input fields.
        const new_form = <HTMLFormElement> this.forms[0].cloneNode(true);
        for (let element of new_form.elements) {
            const form_element = <HTMLInputElement> element;
            form_element.value = "";
        }
        new_form.classList.add('tv-border__top');

        // Check for phone inputs.
        const phone_inputs = new_form.getElementsByClassName('js_phone');
        if (phone_inputs.length > 0) {
            for (const input of phone_inputs) {
                input.addEventListener('input', this.mask_phone_number.bind(this));
            }
        }

        // Add form to the end of the form section list.
        const section_list = this.container.querySelector('#js_' + this.name + '_list');
        section_list.appendChild(new_form);

        this.forms.push(new_form);

    }

    /**
     * Removes the last form from section views form list.
     *
     * @return {void}
     */
    remove_form_from_section(): void {
        const form_count = this.forms.length;
        if (form_count > 1) {
            this.forms[form_count - 1].remove();
            this.forms.splice(form_count - 1, 1);
        }

        // Hide remove button if there is only one form left.
        if (form_count - 1 === 1) {
            this.container.querySelector('#js_' + this.name + '_remove_form_section').classList.add('hidden');
        }
    }
}

/**
 * Class to manage the UI for the credit application form.
 */
export class CreditApplication {
    /**
     * @var {Map<string, CreditAppView>} views dict of the different form section views.
     * @var {HTMLButtonElement} next form button element.
     * @var {HTMLButtonElement} back form button element.
     */
    views: Map<string, CreditAppView>;
    next: HTMLButtonElement;
    back: HTMLButtonElement;
    print: HTMLButtonElement;

    /**
     * Instantiate the class and binds DOM elements.
     *
     * @return {CreditApplication} instantiated and bound.
     */
    constructor() {
        this.views = new Map<string, CreditAppView>();
        this.next = null;
        this.back = null;
        this.print = null;

        this.bind();
    }

    /**
     * Creates views for all of the application sections and binds click events for
     * the next and back buttons.
     *
     * @return {void}
     */
    bind(): void {
        // Find all of the different section containers and create a new view for each of them.
        const containers = document.getElementsByClassName('js-info-container');
        for (const container of containers) {
            const element = <HTMLElement> container;
            const view = new CreditAppView(element.dataset.name, this.views.size + 1);
            // Company info is the first section so we want to make sure that one is visible
            if (view.name === 'company_info') {
                view.visible = true;
            } else if (view.name === 'billing_info') {
                const copy_checkbox = <HTMLInputElement> view.container.querySelector('#js_' + view.name + '_copy');
                copy_checkbox.addEventListener('click', this.copy_company_address.bind(this, view));
            }
            this.views.set(view.name, view);
        }

        this.next = <HTMLButtonElement> document.getElementById('js_next_btn');
        this.next.addEventListener('click', this.next_view.bind(this));

        this.back = <HTMLButtonElement> document.getElementById('js_back_btn');
        this.back.addEventListener('click', this.previous_view.bind(this));
        this.back.classList.add('tv-disabled__button');

        this.print = <HTMLButtonElement> document.getElementById('js_print_btn');
        this.print.addEventListener('click', this.print_application.bind(this));
    }

    /**
     * Event to copy company address fields to the billing info fields.
     *
     * @param {CreditAppView} billing_info view.
     * @param {Event} event from the copy checkbox click event.
     * @return {void}
     */
    copy_company_address(billing_info: CreditAppView, event: Event): void {
        const copy_checkbox = <HTMLInputElement> event.target;

        // We only want to do something if the box has been checked.
        if (!copy_checkbox.checked) {
            return;
        }

        // Get company address fields.
        const company_info = this.get_company_info();

        // Iterated through each of the company info address fields and assign
        // those field values to the billing info address fields.
        company_info.forEach((field, key) => {
            const form_element = <HTMLInputElement> billing_info.forms[0].elements.namedItem(key);
            form_element.value = field.value;
        });
    }

    /**
     * Finds all of the company info views form input fields and assigns them to
     * a map.
     *
     * @return {Map<string, HTMLInputElement}
     */
    get_company_info(): Map<string, HTMLInputElement> {
        const company_info = this.views.get('company_info');
        const contact_info = this.views.get('contact_info');

        if (!company_info) {
            return;
        }

        const info = new Map<string, HTMLInputElement>();
        const fullname = contact_info.forms[0].elements.namedItem('firstname').value + ' ' + contact_info.forms[0].elements.namedItem('lastname').value;
        const contact_name_input = document.getElementById('billing_contact_name') as HTMLInputElement;
        contact_name_input.value = fullname;

        info.set('phone', <HTMLInputElement> contact_info.forms[0].elements.namedItem('phone'));
        info.set('email', <HTMLInputElement> contact_info.forms[0].elements.namedItem('email'));
        info.set('company', <HTMLInputElement> company_info.forms[0].elements.namedItem('company'));
        info.set('street', <HTMLInputElement> company_info.forms[0].elements.namedItem('street'));
        info.set('city', <HTMLInputElement> company_info.forms[0].elements.namedItem('city'));
        info.set('state', <HTMLInputElement> company_info.forms[0].elements.namedItem('state'));
        info.set('zipcode', <HTMLInputElement> company_info.forms[0].elements.namedItem('zipcode'));

        return info;
    }

    /**
     * Finds the section view that is currently visible.
     *
     * @return {CreditAppView}
     */
    find_current_visible(): CreditAppView {
        for (const view of this.views.values()) {
            if (view.visible === true) {
                return view;
            }
        }

        return null;
    }

    /**
     * Cycles UI to the next section view. It first validates the current view's form
     * fields then hides the current section and makes the next section visible.
     * @return {void}
     */
    next_view(): void {
        // Find the current view.
        const current_view = this.find_current_visible();

        // Additional check for Account Number validation
        const account_number_input = document.getElementById('account_number') as HTMLInputElement;
        const account_br_regex = /\d\d\dBR\d\d\d/i;
        const account_no_br_regex = /\d\d\d\d\d\d/i;

        if (!account_number_input.value.match(account_br_regex) && !account_number_input.value.match(account_no_br_regex)) {
            account_number_input.setCustomValidity('Please enter a valid Account Number.');
        } else {
            account_number_input.setCustomValidity('');
        }
        account_number_input.reportValidity();

        // Check to make sure all of the required form fields have been filled out.
        for (const form of current_view.forms) {
            if (!form.checkValidity()) {
                form.reportValidity();
                return;
            }
        }

        // If this was the last section then we want to submit the application.
        if (current_view.index === this.views.size) {
            this.submit();
            return;
        }

        // Hide the current view.
        current_view.toggle_visibility(false);

        // If the back button is disabled we want to enable it.
        if (this.back.classList.contains('tv-disabled__button')) {
            this.back.classList.remove('tv-disabled__button');
        }

        // Make the next view visible and change the next button to the submit
        // button if we are on the last section view.
        const google_captcha = document.querySelector('.js_hide_on_print .js-recaptcha');
        const credit_app_container = document.getElementById('credit-app-button-container');
        for (let view of this.views.values()) {
            if (view.index === current_view.index + 1) {
                view.toggle_visibility(true);
                if (view.index === this.views.size) {
                    this.next.innerHTML = 'Submit';
                    google_captcha.classList.remove('tv-display__none');
                    if (credit_app_container) {
                        credit_app_container.classList.add('span8');
                    }
                }
                break;
            }
        }
    }

    /**
     * Cycles UI to the previous section view. Hides the current view and makes the
     * previous view visible.
     *
     * @return {void}
     */
    previous_view(): void {
        // Change submit button back to next button.
        const credit_app_container = document.getElementById('credit-app-button-container');
        if (this.next.innerHTML === 'Submit') {
            this.next.innerHTML = 'Next';
            if (credit_app_container) {
                credit_app_container.classList.remove('span8');
            }
        }

        // Make current view visible.
        const current_view = this.find_current_visible();
        current_view.toggle_visibility(false);

        // Make the previous view visible and if we are on the first view then
        // we want to disable the back button.
        for (let view of this.views.values()) {
            if (view.index === current_view.index - 1) {
                view.toggle_visibility(true);
                if (view.index === 1) {
                    this.back.classList.add('tv-disabled__button');
                }
                break;
            }
        }
    }

    /**
     * Serializes all of the sections' form data and submits that to the backend controller
     * to submit the credit application.
     *
     * @return {void}
     */
    submit(): void {
        const data = {};
        for (let view of this.views.values()) {
            const form_data = view.serialize_form_data();
            if (form_data.length === 1 && view.name !== 'owner_info') {
                data[view.name] = form_data[0];
                continue;
            }
            data[view.name] = form_data;
        }

        // Push the captcha value into the form data.
        data['captcha'] = grecaptcha.getResponse();

        const url = 'index.php?dispatch=tv_credit_application.submit_application';
        $.ceAjax('request', url, {
            full_render: false,
            method: 'POST',
            caching: false,
            data: data,
            callback: this.application_complete.bind(this)
        });
    }

    /**
     * Hides the application UI and makes the confirmation section visible.
     *
     * @callback
     * @param {Object} response from frontend controller.
     * @return {void}
     */
    application_complete(response: {[key:string]:any}): void {
        if (response.notification[0] === 'E') {
            return;
        }

        document.getElementById('js_credit_application').classList.add('hidden');
        document.getElementById('js_credit_application_success').classList.remove('hidden');

        // Remove Google ReCAPTCHA so it does not show on print doc.
        if (document.getElementsByClassName('g-recaptcha')[0]) {
            (<HTMLElement> document.getElementsByClassName('g-recaptcha')[0]).style.visibility = "hidden";
        }

        // Adjust styling to move Print Button to bottom right.
        document.getElementById('js_credit_application_success').children[0].classList.remove(
            'justify-content__flex-start'
        );
        document.getElementById('js_credit_application_success').children[0].classList.add(
            'justify-content__space-between'
        );
        document.getElementById('js_print_btn').parentElement.classList.add('align-self__flex-end');
    }

    /**
     * Prints the page and uses the print css.
     *
     * @return {void}
     */
    print_application(): void {
        window.print();
    }
}

// Initialize.
export function init() {
    if (document.getElementById('js_credit_application')) {
        // eslint-disable-next-line no-new
        new CreditApplication();
    }
}
